import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login           from './pages/Login';
import ReviewDashboard from './pages/ReviewDashboard';
import AdminDashboard  from './pages/AdminDashboard';
import CompanyOverview from './pages/CompanyOverview';
import Home            from './pages/Home';
import UploadPage      from './pages/UploadPage';
import UserManagement  from './pages/UserManagement';
import AccessLog       from './pages/AccessLog';
import Usage           from './pages/Usage';
import OvertimeDashboard from './pages/OvertimeDashboard';
import OvertimeUpload    from './pages/OvertimeUpload';
import OvertimeDefinitions from './pages/OvertimeDefinitions';
import FuelCardMapping from './pages/FuelCardMapping';
import FuelReport      from './pages/FuelReport';
import Inventory       from './pages/Inventory';
import InventoryFleetio from './pages/InventoryFleetio';
import InventoryCompare from './pages/InventoryCompare';
import InventoryUpload from './pages/InventoryUpload';
import InventoryLocationMapping from './pages/InventoryLocationMapping';
import SalesReport from './pages/SalesReport';
import ContactCenterLeadership from './pages/ContactCenterLeadership';
import ContactCenterExec from './pages/ContactCenterExec';
import ContactCenterVp from './pages/ContactCenterVp';
import ContactCenterAgents from './pages/ContactCenterAgents';
import ContactCenterTeam from './pages/ContactCenterTeam';
import ContactCenterLive from './pages/ContactCenterLive';
import ContactCenterEmail from './pages/ContactCenterEmail';
import ContactCenterSettings from './pages/ContactCenterSettings';
import ContactCenterRepeatCallers from './pages/ContactCenterRepeatCallers';
import ContactCenterCity from './pages/ContactCenterCity';
import ContactCenterOutbound from './pages/ContactCenterOutbound';
import ContactCenterStaffing from './pages/ContactCenterStaffing';
import AscShell from './components/ContactCenter/AscShell';
import TvWall from './pages/TvWall';
import { ccCanOpen, ccCanOpenAnywhere } from './components/ContactCenter/access';
import SalesUpload from './pages/SalesUpload';
import SalesMappings from './pages/SalesMappings';
import StaffingJobReqCategories from './pages/StaffingJobReqCategories';
import JobProfileMapping from './pages/JobProfileMapping';
import CostCenterMapping from './pages/CostCenterMapping';
import TimeWageAudit   from './pages/TimeWageAudit';
import TimeWageAuditUpload from './pages/TimeWageAuditUpload';
import ExpenseAnalysis from './pages/ExpenseAnalysis';
import CustomerBilling from './pages/CustomerBilling';
import CustomerBillingUpload from './pages/CustomerBillingUpload';
import PostCollections from './pages/PostCollections';
import PostCollectionsUpload from './pages/PostCollectionsUpload';
import PostCollectionsRules from './pages/PostCollectionsRules';
import SoftpakReports from './pages/SoftpakReports';
import ScalePak from './pages/ScalePak';
import Disposal from './pages/Disposal';
import FleetReports from './pages/FleetReports';
import FleetDashboard from './pages/FleetDashboard';
import FleetYardMapping from './pages/FleetYardMapping';
import ExpenseUpload   from './pages/ExpenseUpload';
import ExpenseMappings from './pages/ExpenseMappings';
import CityPricing     from './pages/CityPricing';
import StaffingTracker from './pages/StaffingTracker';
import HrHeadcount from './pages/HrHeadcount';
import HrHeadcountUpload from './pages/HrHeadcountUpload';
import StaffingTrackerUpload from './pages/StaffingTrackerUpload';
import StaffingJobRequisitionsUpload from './pages/StaffingJobRequisitionsUpload';
import OvertimeBudgetUpload from './pages/OvertimeBudgetUpload';
import NotFound        from './pages/NotFound';
import AppShell        from './components/Shared/AppShell';
import { moduleAccess, startPath } from './components/Shared/moduleAccess';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/login" replace />;
}

// "/" opens the person's chosen start page when they can still reach it, and
// Home otherwise. It used to send admins and finance to the MOR board and
// everyone else to a MOR review -- so anybody without MOR yards (Contact
// Center, HR, a brand-new account) landed on an empty review page.
function StartRedirect() {
  const auth = useAuth();
  return <Navigate to={startPath(auth.user, moduleAccess(auth))} replace />;
}

function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return (user.role === 'super_admin' || user.role === 'admin')
    ? children
    : <Navigate to="/" replace />;
}

function RequireCompanyView({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return (user.role === 'super_admin' || user.role === 'admin' || user.role === 'finance_reviewer')
    ? children
    : <Navigate to="/" replace />;
}

function RequireSuperAdmin({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return user.role === 'super_admin'
    ? children
    : <Navigate to="/admin" replace />;
}

// Fuel Card Mapping is gated by a standalone permission (canManageFuelCards),
// independent of role — see backend/src/middleware/rbac.js:requireFuelCardAdmin.
// super_admin always has access — super admin has access to everything.
function RequireFuelCardAdmin({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return (user.role === 'super_admin' || user.canManageFuelCards)
    ? children
    : <Navigate to="/" replace />;
}

// Time and Wage Audit is gated by a standalone permission
// (canReviewTimeWageAudit), independent of role — same pattern as Fuel Card
// Mapping above, see backend/src/middleware/rbac.js:requireTimeWageAuditAccess.
// super_admin always has access — super admin has access to everything.
function RequireTimeWageAuditAccess({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return (user.role === 'super_admin' || user.canReviewTimeWageAudit)
    ? children
    : <Navigate to="/" replace />;
}

// Expense Analysis is gated by a standalone permission
// (canViewExpenseAnalysis), independent of role — same pattern as the two
// above, see backend/src/middleware/rbac.js:requireExpenseAnalysisAccess.
// super_admin always has access — super admin has access to everything.
function RequireExpenseAnalysisAccess({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return (user.role === 'super_admin' || user.canViewExpenseAnalysis)
    ? children
    : <Navigate to="/" replace />;
}

// Customer Billing is gated by a standalone permission
// (canViewCustomerBilling), the Expense Analysis shape — see
// backend/src/middleware/rbac.js:requireCustomerBillingAccess.
function RequireCustomerBillingAccess({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return (user.role === 'super_admin' || user.canViewCustomerBilling)
    ? children
    : <Navigate to="/" replace />;
}

// Post Collections is gated by a standalone permission
// (canViewPostCollections), the same shape — see
// backend/src/middleware/rbac.js:requirePostCollectionsAccess.
function RequirePostCollectionsAccess({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return (user.role === 'super_admin' || user.canViewPostCollections)
    ? children
    : <Navigate to="/" replace />;
}

// Fleet Reports: the same shape -- see rbac.js:requireFleetReportsAccess.
function RequireFleetReportsAccess({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return (user.role === 'super_admin' || user.canViewFleetReports)
    ? children
    : <Navigate to="/" replace />;
}

// R&M Dashboard: cost-center scoped, so the server decides who has any access
// (routes/auth.js hasFleetDashboardAccess) and routes/fleetDashboard.js what they see.
function RequireFleetDashboardAccess({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return user.hasFleetDashboardAccess ? children : <Navigate to="/" replace />;
}

// Soft-Pak Reports is gated by a standalone permission (canViewSoftpakReports),
// the same shape -- see backend/src/middleware/rbac.js:requireSoftpakReportsAccess.
function RequireSoftpakReportsAccess({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return (user.role === 'super_admin' || user.canViewSoftpakReports)
    ? children
    : <Navigate to="/" replace />;
}

// Scale-Pak is its own standalone permission (canViewScalePak), all or nothing --
// see backend/src/middleware/rbac.js:requireScalePakAccess.
function RequireScalePakAccess({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return (user.role === 'super_admin' || user.canViewScalePak)
    ? children
    : <Navigate to="/" replace />;
}

// Disposal is its own standalone permission (canViewDisposal), all or nothing --
// see backend/src/middleware/rbac.js:requireDisposalAccess.
function RequireDisposalAccess({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return (user.role === 'super_admin' || user.canViewDisposal)
    ? children
    : <Navigate to="/" replace />;
}

// City Pricing is gated by a standalone permission (canManagePriceSheets /
// canViewPriceSheets), independent of role — see
// backend/src/middleware/rbac.js:requirePriceSheetsManage/View. super_admin
// always has access. For now nobody else has been granted either
// permission, so only super_admin can reach this.
function RequirePriceSheetsAccess({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return (user.role === 'super_admin' || user.canManagePriceSheets || user.canViewPriceSheets)
    ? children
    : <Navigate to="/" replace />;
}

// Staffing Tracker is visible to unrestricted roles (super_admin/admin — NOT
// finance_reviewer, unlike Overtime/Fuel/Inventory), anyone granted the
// standalone can_view_staffing_tracker permission (HR/Recruiting), or anyone
// with a user_overtime_cost_centers assignment (DVPs — reused from
// Overtime/Fuel/Inventory, same as hasOvertimeAccess already computes).
function RequireStaffingAccess({ children }) {
  const { user, isAdmin, hasOvertimeAccess } = useAuth();
  if (!user) return null;
  return (isAdmin || user.canViewStaffingTracker || hasOvertimeAccess)
    ? children
    : <Navigate to="/" replace />;
}

// HR Headcount is visible to unrestricted roles, to anyone holding either
// standalone HR permission, or to anyone with a user_overtime_cost_centers
// assignment (DVPs see their own yards — the route scopes the rows). Same
// shape as RequireStaffingAccess above.
function RequireHrHeadcountAccess({ children }) {
  const { user, isAdmin, hasOvertimeAccess } = useAuth();
  if (!user) return null;
  return (isAdmin || user.canViewHrHeadcount || user.canViewHrAllocations || hasOvertimeAccess)
    ? children
    : <Navigate to="/" replace />;
}

// Sales Report follows what the SERVER says about this person
// (routes/auth.js, from services/salesAccess.js) rather than re-deriving it --
// the same function decides what the board returns, so the route and the data
// cannot disagree about who gets in.
function RequireSalesReportAccess({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return user.hasSalesReportAccess ? children : <Navigate to="/" replace />;
}

// Contact Center pages follow what the SERVER says this person may open
// (user.contactCenter, from services/ccAccess.js via /auth/me): the same
// function gates the data, so the route and the data cannot disagree.
function RequireCcView({ view, dept, children }) {
  const { user } = useAuth();
  if (!user) return null;
  return ccCanOpen(user, view, dept) ? children : <Navigate to="/" replace />;
}

// Live Ops, Agents, Team Performance and Outbound answer for whichever
// department is asked for, so the route opens when the person holds the page
// in ANY department; the page itself defaults to one they hold.
function RequireCcAnywhere({ view, children }) {
  const { user } = useAuth();
  if (!user) return null;
  return ccCanOpenAnywhere(user, view) ? children : <Navigate to="/" replace />;
}

// Settings follow the manage permission, not a page grant.
function RequireCcManage({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return user.contactCenter?.canManage ? children : <Navigate to="/" replace />;
}

function AppRoutes() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F3F4F6',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid #E5E7EB',
            borderTop: '3px solid #1B2A4A',
            borderRadius: '50%',
            animation: 'spin 0.7s linear infinite',
            margin: '0 auto 12px',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* A TV wall cannot sign in: it pairs as a display and reads only its
          wall (pages/TvWall.jsx, routes/contactCenterTv.js). */}
      <Route path="/tv" element={<TvWall />} />
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route index element={<StartRedirect />} />
        <Route path="home" element={<Home />} />
        <Route path="review"                         element={<ReviewDashboard />} />
        <Route path="review/:costCenterId/:periodId" element={<ReviewDashboard />} />
        <Route path="admin"              element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
        <Route path="admin/company"            element={<RequireCompanyView><CompanyOverview /></RequireCompanyView>} />
        <Route path="admin/company/:periodId" element={<RequireCompanyView><CompanyOverview /></RequireCompanyView>} />
        <Route path="admin/upload"       element={<RequireSuperAdmin><UploadPage /></RequireSuperAdmin>} />
        <Route path="admin/users"        element={<RequireSuperAdmin><UserManagement /></RequireSuperAdmin>} />
        <Route path="admin/access-log"   element={<RequireSuperAdmin><AccessLog /></RequireSuperAdmin>} />
        <Route path="admin/usage"        element={<RequireSuperAdmin><Usage /></RequireSuperAdmin>} />
        <Route path="overtime"           element={<OvertimeDashboard />} />
        <Route path="overtime/definitions" element={<OvertimeDefinitions />} />
        <Route path="overtime/upload"    element={<RequireSuperAdmin><OvertimeUpload /></RequireSuperAdmin>} />
        <Route path="admin/fuel-cards"   element={<RequireFuelCardAdmin><FuelCardMapping /></RequireFuelCardAdmin>} />
        <Route path="fuel-report"        element={<FuelReport />} />
        <Route path="inventory"          element={<Inventory />} />
        <Route path="inventory-fleetio"  element={<RequireSuperAdmin><InventoryFleetio /></RequireSuperAdmin>} />
        <Route path="inventory-fleetio/compare" element={<RequireSuperAdmin><InventoryCompare /></RequireSuperAdmin>} />
        <Route path="inventory/upload"   element={<RequireSuperAdmin><InventoryUpload /></RequireSuperAdmin>} />
        <Route path="admin/inventory-locations" element={<RequireSuperAdmin><InventoryLocationMapping /></RequireSuperAdmin>} />
        <Route path="admin/job-profiles" element={<RequireSuperAdmin><JobProfileMapping /></RequireSuperAdmin>} />
        <Route path="admin/cost-centers" element={<RequireSuperAdmin><CostCenterMapping /></RequireSuperAdmin>} />
        <Route path="time-wage-audit"    element={<RequireTimeWageAuditAccess><TimeWageAudit /></RequireTimeWageAuditAccess>} />
        {/* NOT RequireSuperAdmin, unlike every other upload route: this
            module's ingestion endpoints are gated by requireTimeWageAuditAccess,
            so a reviewer holding that standalone permission can already upload.
            A super-admin guard here would hide a page the API lets them use. */}
        <Route path="time-wage-audit/upload" element={<RequireTimeWageAuditAccess><TimeWageAuditUpload /></RequireTimeWageAuditAccess>} />
        <Route path="expense-analysis"   element={<RequireExpenseAnalysisAccess><ExpenseAnalysis /></RequireExpenseAnalysisAccess>} />
        {/* Super admin, not RequireExpenseAnalysisAccess: POST /api/expenses/upload
            and the commit/cancel endpoints are requireRole('super_admin'), so a
            view-only expense user reaching this page would only find a 403. */}
        <Route path="expense-analysis/upload" element={<RequireSuperAdmin><ExpenseUpload /></RequireSuperAdmin>} />
        {/* Reference data behind the dashboard. Viewable by anyone with
            Expense Analysis access; the write actions inside are super-admin
            only and gated server-side. */}
        <Route path="admin/expense-mappings" element={<RequireExpenseAnalysisAccess><ExpenseMappings /></RequireExpenseAnalysisAccess>} />
        <Route path="customer-billing"   element={<RequireCustomerBillingAccess><CustomerBilling /></RequireCustomerBillingAccess>} />
        {/* Super admin, like every other upload: POST /api/customer-billing/upload
            is requireRole('super_admin') inside a router a viewer can reach. */}
        <Route path="customer-billing/upload" element={<RequireSuperAdmin><CustomerBillingUpload /></RequireSuperAdmin>} />
        <Route path="post-collections"   element={<RequirePostCollectionsAccess><PostCollections /></RequirePostCollectionsAccess>} />
        <Route path="scale-pak"          element={<RequireScalePakAccess><ScalePak /></RequireScalePakAccess>} />
        <Route path="disposal"           element={<RequireDisposalAccess><Disposal /></RequireDisposalAccess>} />
        <Route path="softpak-reports"    element={<RequireSoftpakReportsAccess><SoftpakReports /></RequireSoftpakReportsAccess>} />
        <Route path="fleet-dashboard"    element={<RequireFleetDashboardAccess><FleetDashboard /></RequireFleetDashboardAccess>} />
        <Route path="admin/fleet-yards" element={<RequireSuperAdmin><FleetYardMapping /></RequireSuperAdmin>} />
        <Route path="fleet-reports"      element={<RequireFleetReportsAccess><FleetReports /></RequireFleetReportsAccess>} />
        {/* Super admin, like every other upload: POST /api/post-collections/upload
            is requireRole('super_admin') inside a router a viewer can reach. */}
        <Route path="post-collections/upload" element={<RequireSuperAdmin><PostCollectionsUpload /></RequireSuperAdmin>} />
        {/* Super admin: a rule restates every day the report shows, and every
            /post-collections/rules endpoint is requireRole('super_admin'). */}
        <Route path="post-collections/rules" element={<RequireSuperAdmin><PostCollectionsRules /></RequireSuperAdmin>} />
        <Route path="pricing" element={<RequirePriceSheetsAccess><CityPricing /></RequirePriceSheetsAccess>} />
        <Route path="staffing-tracker"   element={<RequireStaffingAccess><StaffingTracker /></RequireStaffingAccess>} />
        <Route path="hr-headcount"       element={<RequireHrHeadcountAccess><HrHeadcount /></RequireHrHeadcountAccess>} />
        <Route path="admin/hr-headcount-upload" element={<RequireSuperAdmin><HrHeadcountUpload /></RequireSuperAdmin>} />
        <Route path="admin/staffing-tracker-upload" element={<RequireSuperAdmin><StaffingTrackerUpload /></RequireSuperAdmin>} />
        <Route path="admin/staffing-job-requisitions-upload" element={<RequireSuperAdmin><StaffingJobRequisitionsUpload /></RequireSuperAdmin>} />
        <Route path="admin/staffing-job-req-categories" element={<RequireSuperAdmin><StaffingJobReqCategories /></RequireSuperAdmin>} />
        <Route path="admin/overtime-budget" element={<RequireSuperAdmin><OvertimeBudgetUpload /></RequireSuperAdmin>} />
        <Route path="sales-report"       element={<RequireSalesReportAccess><SalesReport /></RequireSalesReportAccess>} />
        {/* ASC Dashboard — one sidebar link, the pages as tabs inside it
            (AscShell). Every URL is what it was: the routes are nested, not
            renamed, so a bookmark or a link in an email still works. Settings
            is not a tab; it sits with the portal's other administration
            pages. */}
        <Route path="contact-center" element={<AscShell />}>
          <Route index element={null} />
          <Route path="live" element={<RequireCcAnywhere view="live_ops"><ContactCenterLive /></RequireCcAnywhere>} />
          <Route path="agents" element={<RequireCcAnywhere view="agents"><ContactCenterAgents /></RequireCcAnywhere>} />
          {/* One screen, three grants (migration 075). Director and Manager
              are the same page, as they were in the old app; Supervisor ranks
              the table and opens on one pod. /team stays for anyone whose
              department still carries the merged grant. */}
          <Route path="director" element={<RequireCcAnywhere view="director"><ContactCenterTeam variant="director" /></RequireCcAnywhere>} />
          <Route path="manager" element={<RequireCcAnywhere view="manager"><ContactCenterTeam variant="manager" /></RequireCcAnywhere>} />
          <Route path="supervisor" element={<RequireCcAnywhere view="supervisor"><ContactCenterTeam variant="supervisor" /></RequireCcAnywhere>} />
          <Route path="team" element={<RequireCcAnywhere view="team_performance"><ContactCenterTeam /></RequireCcAnywhere>} />
          <Route path="staffing" element={<RequireCcView view="staffing" dept="support"><ContactCenterStaffing /></RequireCcView>} />
          <Route path="repeat-callers" element={<RequireCcView view="repeat_callers" dept="support"><ContactCenterRepeatCallers /></RequireCcView>} />
          <Route path="city" element={<RequireCcView view="city" dept="support"><ContactCenterCity /></RequireCcView>} />
          <Route path="email" element={<RequireCcView view="email" dept="support"><ContactCenterEmail /></RequireCcView>} />
          <Route path="outbound" element={<RequireCcAnywhere view="outbound"><ContactCenterOutbound /></RequireCcAnywhere>} />
          <Route path="leadership" element={<RequireCcView view="leadership" dept="support"><ContactCenterLeadership /></RequireCcView>} />
          <Route path="exec" element={<RequireCcView view="exec" dept="support"><ContactCenterExec /></RequireCcView>} />
          <Route path="vp" element={<RequireCcView view="vp" dept="support"><ContactCenterVp /></RequireCcView>} />
        </Route>
        <Route path="contact-center/settings" element={<RequireCcManage><ContactCenterSettings /></RequireCcManage>} />
        <Route path="sales-report/upload" element={<RequireSuperAdmin><SalesUpload /></RequireSuperAdmin>} />
        <Route path="admin/sales-mappings" element={<RequireSuperAdmin><SalesMappings /></RequireSuperAdmin>} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}